import React, { useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { useCreateGiftCardMutation, useUpdateGiftCardMutation } from "../../../../../redux/apiSlice";
import cogoToast from "cogo-toast";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Form as FormLayout } from "react-bootstrap";
import TextInput from "../../../Common/components/text-input";
import TextAreaInput from "../../../Common/components/text-area-input";
import { GiftCardIcon } from "../../../../util/Icons";
import * as handlers from "../../../Instrument/_redux/instruments/instrumentsHandlers"
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import SearchInput from "../../../Common/components/search-input";

const EditGiftCardModal = ({ showEditGiftCardModal, userGroup, details, onHide, tableRef }) => {
    const instruments = useSelector(({ instruments }) => instruments, shallowEqual);

    const dispatch = useDispatch();

    /* Instrument Search Input */
    const searchInstrument = query => {
        if (query !== "") {
            dispatch(handlers.searchInstruments(query));
        }
    }

    const assetOptions = useMemo(() => {
        return instruments?.data?.map((asset) => {
            return { name: asset?.symbol, value: asset?.instrumentId }
        })
    }, [instruments?.data])

    /* Notification Pop up */
    useEffect(() => {
        if (instruments.notification) {
            cogoToast[instruments.notification.type](instruments.notification.message);
            dispatch(handlers.clearNotification());
        }
    });

    const isUpdate = !!details

    const [
        createGiftCard,
        {
            isLoading: isCreating,
        }
    ] = useCreateGiftCardMutation()

    const [
        updateGiftCard,
        {
            isLoading: isUpdating,
        }
    ] = useUpdateGiftCardMutation()

    const validationSchema = Yup.object({
        instrumentId: Yup.string().required('Required'),
        amount: Yup.number().min(0.0).required('Required'),
        vendor: Yup.string().required('Required'),
        description: Yup.string().required('Required'),
        colour: Yup.string().required('Required'),
        ...isUpdate ? {} : { quantity: Yup.number().min(0.0).required('Required') },
    })

    return (
        <Modal
            show={showEditGiftCardModal}
            onHide={onHide}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={(isCreating || isUpdating) ? "static" : true}
            keyboard={!(isCreating || isUpdating)}
        >
            <Formik
                initialValues={details || {}}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    values["userGroup"] = userGroup

                    if (isUpdate) {
                        updateGiftCard({ ...values, meta: details })
                            .unwrap()
                            .then(() => {
                                cogoToast.success("Created 'Update Gift Card' action successfully")
                                tableRef.current.refresh()
                                onHide()
                            })
                    } else {
                        createGiftCard(values)
                            .unwrap()
                            .then(() => {
                                cogoToast.success("Created 'Create Gift Card' action successfully")
                                tableRef.current.refresh()
                                onHide()
                            })
                    }
                }}
            >
                {
                    ({ isValid }) => (
                        <Form>
                            <Modal.Header>
                                <Modal.Title id="contained-modal-title-vcenter">
                                    <span className={`svg-icon svg-icon-lg svg-icon-primary mr-3`}>
                                        <GiftCardIcon />
                                    </span>
                                    {isUpdate ? `Edit ${details.id}` : `Create Gift Card`}
                                </Modal.Title>
                                <button type="button" className="close" onClick={onHide}>
                                    <span aria-hidden="true" className="d-block">×</span>
                                    <span className="sr-only">Close</span>
                                </button>
                            </Modal.Header>
                            <Modal.Body
                                className="px-10 py-2"
                            >
                                <fieldset disabled={isCreating || isUpdating}>
                                    <FormLayout.Row>
                                        <TextInput
                                            label="Vendor*"
                                            name="vendor"
                                            type="text"
                                        />
                                        <TextInput
                                            label="Colour*"
                                            name="colour"
                                            type="text"
                                        />
                                    </FormLayout.Row>
                                    <FormLayout.Row>
                                        <TextAreaInput
                                            label="Description*"
                                            name="description"
                                        />
                                    </FormLayout.Row>
                                    <FormLayout.Row>
                                        <TextInput
                                            label="Amount*"
                                            name="amount"
                                            type="number"
                                            step="any"
                                            min="0.0"
                                        />
                                        <TextInput
                                            label={`Number of Cards${isUpdate ? '' : '*'}`}
                                            name="quantity"
                                            type="number"
                                            step="any"
                                            min="0.0"
                                        />
                                    </FormLayout.Row>
                                    <FormLayout.Row>
                                        <SearchInput
                                            label="Asset*"
                                            name="instrumentId"
                                            placeholder="Select Asset"
                                            onInput={searchInstrument}
                                            isLoading={instruments?.isLoading}
                                            initialSelection={isUpdate ? { name: details?.asset?.symbol, value: details?.instrumentId } : null}
                                            options={assetOptions}
                                        />
                                    </FormLayout.Row>
                                </fieldset>
                            </Modal.Body>
                            <Modal.Footer>
                                <button
                                    type="button"
                                    onClick={onHide}
                                    className="btn btn-light btn-elevate px-4"
                                    disabled={isCreating || isUpdating}
                                >
                                    Close
                                </button>
                                <> </>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-elevate px-4 ml-4"
                                    disabled={isCreating || isUpdating || !isValid}
                                >
                                    {isUpdate ? `Update Gift Card` : `Create Gift Card`}
                                </button>
                            </Modal.Footer>
                        </Form>
                    )}
            </Formik>
        </Modal>
    );
}

export default EditGiftCardModal;